import * as React from 'react'
// components
import Grid from '@mui/material/Grid2'
import { AvatarsList, Button, Menu } from 'components/UI'
// views
import {
  CardHeader,
  CardInnerWrapper,
  CardText,
  CardWrapper,
  Chip,
  DotsIcon,
  ViewButtonWrapper
} from 'components/Modals/PlanItems/components/ReviewsList/views'
import { AvatarWrapper } from 'components/Modals/PlanItems/components/CompleteReview/views'
// types
import { ReviewStatus } from 'types/graphql'
// hooks
import { useCurrentUser } from 'hooks/index'
// themes
import { colors } from 'themes'
// icons
import EditIcon from '@mui/icons-material/EditOutlined'
import WatchIcon from '@mui/icons-material/WatchLaterOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined'
// utils
import get from 'lodash/get'
import moment from 'moment'
import { getReviewDueInfo, isUserReviewer, TGeneratedReview } from 'utils/reviews'

export interface IReviewCardActions {
  onStartReview: (review: TGeneratedReview) => void
  onShowReviewDetails: (review: TGeneratedReview) => void
  onEditScheduledReview: (review: TGeneratedReview) => void
  onDeleteReview: (reviewId: string) => void
}

interface IIncomingProps {
  review: TGeneratedReview
  entity: any
  isSidebar?: boolean
  canDelete: boolean
  canModify: boolean
  actions: IReviewCardActions
}

const getDueTitle = (review) => {
  const { isDueToday, isOverdue, isDueTomorrow } = getReviewDueInfo(review)

  if (isDueToday) {
    return 'Due Today'
  }
  if (isOverdue) {
    return 'Overdue'
  }
  if (isDueTomorrow) {
    return 'Due Tomorrow'
  }
  return `Due in ${review.dueDate.diff(moment().startOf('day'), 'days')} days`
}

const ReviewCard: React.FC<IIncomingProps> = ({ review, entity, isSidebar, canDelete, canModify, actions }) => {
  const [isHovered, setHovered] = React.useState(false)

  const currentUser = useCurrentUser()

  const { isDueToday, isOverdue } = getReviewDueInfo(review)

  const reviewers = get(review, 'scheduledReview.scheduledReviewers')
  const reviewStatus = get(review, 'status', ReviewStatus.NotStarted)
  const isCurrentUserReviewer = isUserReviewer(review, currentUser)
  const isReviewComplete = reviewStatus === ReviewStatus.Complete
  const canModifyReview = canModify && entity.scheduledReviews.some(({ id }) => id === review.scheduledReview.id)

  const onMouseEnter = () => {
    if (reviewStatus !== ReviewStatus.NotStarted) {
      setHovered(true)
    }
  }

  const onMouseLeave = () => {
    if (reviewStatus !== ReviewStatus.NotStarted) {
      setHovered(false)
    }
  }

  const showReviewResult = () => actions.onShowReviewDetails(review)

  const startReview = () => actions.onStartReview(review)

  const onEditButtonClick = () => actions.onEditScheduledReview(review)

  const onDeleteButtonClick = () => actions.onDeleteReview(review.id)

  return (
    <CardWrapper data-testid='review-card' data-entity-id={review.id}>
      <CardHeader backgroundColor={colors.darkBlue500} reviewStatus={reviewStatus}>{`${review.startPeriod.format(
        'D MMM Y'
      )} to ${review.endPeriod.format('D MMM Y')}`}</CardHeader>
      <CardInnerWrapper
        container
        direction={'column'}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        justifyContent={'space-between'}
        isReviewComplete={isReviewComplete}>
        <Grid container justifyContent={'space-between'}>
          <Grid container direction={'column'} justifyContent={'space-around'}>
            {!isReviewComplete && <CardText>{getDueTitle(review)}</CardText>}
            <span>{review.approvedAt ? ReviewStatus.Approved : reviewStatus.replace('_', ' ')}</span>
          </Grid>

          {!isReviewComplete && isCurrentUserReviewer && (
            <Grid container alignItems={'baseline'} justifyContent={'flex-end'}>
              <Button
                onClick={startReview}
                style={{ zIndex: 8 }}
                disabled={!moment().isSameOrAfter(review.startPeriod, 'day')}>
                {reviewStatus === ReviewStatus.InProgress ? 'Finish Review' : 'Start Review'}
              </Button>
            </Grid>
          )}
        </Grid>

        <Grid container alignItems={'flex-end'}>
          <Chip
            isDueLate={isOverdue}
            isDueToday={isDueToday}
            isReviewComplete={isReviewComplete}
            label={review.dueDate.format('D MMMM')}
            icon={<WatchIcon style={{ color: colors.white }} />}
          />

          <AvatarWrapper container>
            <AvatarsList list={reviewers} limit={isSidebar ? 6 : 11} />
          </AvatarWrapper>

          {((canDelete && reviewStatus !== ReviewStatus.NotStarted) || canModify) && (
            <Menu trigger={<DotsIcon htmlColor='black' />}>
              {canModifyReview && (
                <span onClick={onEditButtonClick}>
                  <EditIcon />
                  Edit Schedule
                </span>
              )}
              {canDelete && reviewStatus !== ReviewStatus.NotStarted && (
                <span onClick={onDeleteButtonClick}>
                  <DeleteIcon />
                  Delete
                </span>
              )}
            </Menu>
          )}
        </Grid>

        {isHovered && reviewStatus !== ReviewStatus.NotStarted && (
          <ViewButtonWrapper>
            <Button shadowLess onClick={showReviewResult}>
              View
            </Button>
          </ViewButtonWrapper>
        )}
      </CardInnerWrapper>
    </CardWrapper>
  )
}

export default ReviewCard
